/**
 * Shared type definitions for the LCDD Catalogue PWA.
 * Field names match backend API snake_case exactly.
 */

export type ItemRow = {
  itemid: number;
  itemcode?: string | null;
  itemdescription?: string | null;
  itemcategory?: string | null;
  itemislive?: boolean | null;
  pack_size?: number | null;
  final_weight?: number | null;
  item_is_gluten: boolean;
  item_is_dairy: boolean;
  item_is_egg: boolean;
  item_is_nuts: boolean;
  item_is_soya: boolean;
  item_is_fish: boolean;
  item_is_celery: boolean;
  item_is_mustard: boolean;
  item_is_sesame: boolean;
  item_is_sulphites: boolean;
  item_is_lupin: boolean;
  item_is_mollusc: boolean;
  item_is_vegetarian: boolean;
  item_is_vegan: boolean;
  item_is_halal: boolean;
  item_ingredientlist?: string | null;
  item_marketingdescription?: string | null;
  item_marketingproducttitle?: string | null;
  item_webrelease?: boolean | null;
  item_webtopcategory?: string | null;
  category5id_fk?: number | null;
  category5_description?: string | null;
  category6id_fk?: number | null;
  category6_description?: string | null;
  item_webprice?: number | null;
  item_webdefrost?: string | null;
  item_webreheat?: string | null;
  item_webshelflife?: string | null;
  int_life_2?: number | null;
  item_handling_guideline?: string | null;
  // Auto-translated fields (present only when DEEPL_API_KEY is configured on-prem)
  itemdescription_fr?: string | null;
  itemdescription_es?: string | null;
  item_ingredientlist_fr?: string | null;
  item_ingredientlist_es?: string | null;
  item_handling_guideline_fr?: string | null;
  item_handling_guideline_es?: string | null;
  item_marketingproducttitle_fr?: string | null;
  item_marketingproducttitle_es?: string | null;
  item_marketingdescription_fr?: string | null;
  item_marketingdescription_es?: string | null;
  item_trac?: string | null;
  item_spec_date?: string | null;
  item_lastupdated?: string | null;
  itemcustomer?: number | null;
  customername?: string | null;
  itemstdbatch?: number | null;
  int_life?: number | null;
  item_lifedelivery?: number | null;
};

export type Category5Row = {
  category5id: number;
  category5_description: string | null;
  category5_notes: string | null;
};

export type CompanyRow = {
  companyid: number;
  companyname: string | null;
};

export type CustomerItemRow = {
  customeritemsid: number;
  companyid_fk: number;
  itemid_fk: number;
  customeritems_salesprice: number | null;
  customeritems_live: boolean;
  code: string | null;
};

export type CatalogueColours = {
  primary_dark: string;
  primary_purple: string;
  primary_pink: string;
  primary_gold: string;
  supplementary_light_yellow: string;
  supplementary_light_pink: string;
  supplementary_lavender: string;
  supplementary_grey: string;
  supplementary_light_grey: string;
};

export type CatalogueSettings = {
  brand_name: string;
  brand_subtitle: string;
  font_family: string;
  colours: CatalogueColours;
  logo_file: string;
  catalogue_banner_file: string;
  catalogue_banner_folder: string;
  header_shapes_file: string;
  font_files: {
    regular: string;
    bold: string;
    semibold: string;
    light: string;
    italic: string;
    bold_italic: string;
  };
};

export const DEFAULT_CATALOGUE_SETTINGS: CatalogueSettings = {
  brand_name: 'Didier Patisserie',
  brand_subtitle: 'by Didier Merveilleux',
  font_family: 'Titillium Web',
  colours: {
    primary_dark: '#363D4C',
    primary_purple: '#A55DA0',
    primary_pink: '#F1C3DB',
    primary_gold: '#FCBC7A',
    supplementary_light_yellow: '#FEE2C3',
    supplementary_light_pink: '#F9E5EF',
    supplementary_lavender: '#EDE5ED',
    supplementary_grey: '#C4C2C7',
    supplementary_light_grey: '#F7F6F2',
  },
  logo_file: 'Didier_Grey.jpg',
  catalogue_banner_file: 'HEADER WEBSITE Trac-IT.png',
  catalogue_banner_folder: 'ARTWORK',
  header_shapes_file: 'shapes pink.png',
  font_files: {
    regular: 'TitilliumWeb-Regular.ttf',
    bold: 'TitilliumWeb-Bold.ttf',
    semibold: 'TitilliumWeb-SemiBold.ttf',
    light: 'TitilliumWeb-Light.ttf',
    italic: 'TitilliumWeb-Italic.ttf',
    bold_italic: 'TitilliumWeb-BoldItalic.ttf',
  },
};

export type SyncMeta = {
  id: 'main';
  lastSyncedAt: number | null;
};

/** Airline catalogue tier (stored per item in IndexedDB when Airline Mode is used). */
export type AirlineTier = 'first' | 'business' | 'economy';

export const AIRLINE_TIERS: { value: AirlineTier; label: string }[] = [
  { value: 'first', label: 'First Class' },
  { value: 'business', label: 'Business Class' },
  { value: 'economy', label: 'Economy Class' },
];

export const AIRLINE_TIER_ORDER: Record<AirlineTier, number> = {
  first: 0,
  business: 1,
  economy: 2,
};
